import { defaultTransform } from '../../core/geometry';
import type { SceneItem } from '../../core/model';
import { isSystemItem, pinSystemItems, SYSTEM_SOURCE_NAME } from '../../core/system-items';
import type { StudioApi } from '../../engine/api';
import { createId, defaultAudioStrip } from '../../state';

const PLUGIN_ID = 'com.ohmypeer.organic-ads';
const AUDIO_KIND = `${PLUGIN_ID}.audio`;
const SLOT_KIND = `${PLUGIN_ID}.slot`;
type Studio = Pick<StudioApi, 'project' | 'registry' | 'loadPlugin' | 'notify' | 'on'>;

/** Prepares the production scene only; approval and delivery remain the executor's responsibility. */
export function createMonetizationSync(studio: Studio) {
  let enabled = false;
  let disposed = false;
  let updating = false;
  let pending: Promise<void> | null = null;
  let failed = false;
  let scheduled = false;
  let opening = false;

  const ensureScenes = () => {
    if (!enabled || disposed || updating || opening) return;
    const audio = studio.registry.getSource(AUDIO_KIND);
    const slot = studio.registry.getVideoFilter(SLOT_KIND);
    if (!audio || !slot) return;
    updating = true;
    try {
      studio.project.mutate('Prepare monetization', project => {
        const containsAd = (items: readonly SceneItem[]): boolean => items.some(item => isSystemItem(project, item) || (item.type === 'group' && containsAd(item.children)));
        let source = Object.values(project.sources).find(s => s.kind === AUDIO_KIND);
        for (const scene of Object.values(project.scenes)) {
          if (!containsAd(scene.items)) {
            if (!source) {
              const id = createId();
              source = { id, name: SYSTEM_SOURCE_NAME, kind: AUDIO_KIND, settings: audio.defaults(), filters: [], audio: defaultAudioStrip() };
              project.sources[id] = source;
            }
            scene.items.push({ id: createId(), type: 'source', sourceId: source.id, visible: true, locked: true, transform: defaultTransform(), blendMode: 'normal', scaleFilter: 'default', opacity: 1 });
          }
          if (!scene.filters.some(f => f.kind === SLOT_KIND)) {
            scene.filters.push({ id: createId(), kind: SLOT_KIND, name: slot.displayName, settings: slot.defaults(), enabled: true });
          }
        }
        pinSystemItems(project);
      }, { undoable: false });
    } finally {
      updating = false;
    }
  };
  const reconcile = async (): Promise<void> => {
    if (!enabled || disposed || opening) return;
    const ref = studio.project.getState().project.plugins.find(p => p.id === PLUGIN_ID && p.enabled);
    if (!pending && !failed && (!studio.registry.getSource(AUDIO_KIND) || !ref)) {
      pending = (async () => {
        try {
          await studio.loadPlugin('/plugins/organic-ads/1.0.0/index.js');
        } catch (error) {
          failed = true;
          if (!disposed) studio.notify('error', `Could not prepare OMP Advertising: ${error instanceof Error ? error.message : String(error)}`);
        }
      })();
    }
    const loading = pending;
    await loading;
    if (pending === loading) pending = null;
    ensureScenes();
  };
  // Wait for the engine to finish collection plugin changes before loading or mutating its document.
  const schedule = () => {
    if (scheduled || updating || disposed || !enabled) return;
    scheduled = true;
    queueMicrotask(() => { scheduled = false; ensureScenes(); });
  };
  const unsubscribe = studio.project.subscribe(schedule);
  const unsubscribeOpened = studio.on('project-opened', () => { opening = true; });
  const unsubscribePlugins = studio.on('plugins-synchronized', () => { opening = false; failed = false; void reconcile(); });
  return {
    async setEnabled(next: boolean): Promise<void> {
      if (disposed) return;
      enabled = next;
      failed = false;
      await reconcile();
    },
    dispose() { disposed = true; unsubscribe(); unsubscribeOpened(); unsubscribePlugins(); },
  };
}
